/* eslint-disable react/no-unescaped-entities */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
  Button,
  Flex,
  LoadingOverlay, Select, Text, TextInput, Tooltip,
} from "@mantine/core";
import { useValidatedState } from "@mantine/hooks";
import { useLogoutFunction } from "@propelauth/react";
import { currencies } from "currencies.json";
import { useEffect, useState } from "react";
import {
  useNavigate,
} from "react-router-dom";
import TimezoneSelect from "react-timezone-select";
import validator from "validator";
import { IconInfoCircle, IconArrowRight, IconLogout } from "@tabler/icons-react";
import { queryClient } from "../../../hooks/QueryHooks";
import useSelectWorkspace from "../../../hooks/useSelectWorkspace";
import { createApiKey } from "../../../services/apiKeyEndpoint";
import { updateWorkspace } from "../../../services/workspace/workspaceEndpoints";
import notify from "../../../utils/notify";

const OnboardingWorkspace = () => {
  const { data, isLoading } = useSelectWorkspace();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState<string | null>("USD");
  const [{ value: trackingDomain, valid }, setTrackingDomain] = useValidatedState(
    "https://",
    // eslint-disable-next-line no-useless-escape
    (value) => (!validator.isURL(value)),
    true,
  );

  const [timezone, setTimezone] = useState<any>(Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || "");
  const logout = useLogoutFunction();

  const isFormValid = currency && timezone && trackingDomain && trackingDomain !== "https://";

  const submitHandler = async () => {
    if (data?.id) {
      try {
        await Promise.all([await updateWorkspace({
          workspace_id: data?.id,
          workspace_time_zone: timezone?.value || timezone,
          is_default_workspace: true,
          workspace_default_currency: currency || "USD",
          tracking_website_domain: trackingDomain,
        }),
        await createApiKey({ workspace_id: data?.id })]);
        notify({
          type: "success",
          messageList: ["Successfully updated workspace"],
        });
        queryClient.setQueryData(["workspace", data?.id], (oldData: any) => ({
          ...oldData,
          data: {
            ...oldData?.data,
            workspace_time_zone: timezone?.value || timezone,
            is_default_workspace: true,
            workspace_default_currency: currency || "USD",
            tracking_website_domain: trackingDomain,
          },
        }));
        navigate(`/onboarding/workspace/${data?.unique_identifier}/integrations`, { replace: true });
      } catch (err) {
        notify({
          heading: "Workspace creation failed",
          type: "failure",
          messageList: ["Sorry for the inconvenience. Kindly contact support to help you with this issue"],
        });
      }
    } else {
      notify({
        heading: "Failed to update workspace",
        type: "failure",
        messageList: ["The provided workspace id is invalid"],
      });
    }
  };

  useEffect(() => {
    if (data?.tracking_website_domain
      && data?.workspace_default_currency && data?.workspace_time_zone) {
      navigate(`/onboarding/workspace/${data?.unique_identifier}/integrations`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.tracking_website_domain, data?.workspace_default_currency, data?.workspace_time_zone]);

  return (
    <section className="flex flex-col rounded-lg border border-[#EEEEEE] p-10 max-w-[540px] w-full bg-white relative"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}
    >
      <LoadingOverlay
        loaderProps={{ size: "lg" }}
        c="main.5"
        visible={isLoading || (data?.tracking_website_domain
          && data?.workspace_default_currency && data?.workspace_time_zone)}
        overlayProps={{
          blur: 10,
        }}
      />

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-medium"
          style={{ backgroundColor: "#8267E6" }}>1</span>
        <span className="text-xs font-medium" style={{ color: "#5C6070" }}>
          Workspace setup
        </span>
        <span className="h-px flex-1" style={{ backgroundColor: "#EEEEEE" }} />
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium border"
          style={{ borderColor: "#D0D0D0", color: "#B0B5C0" }}>2</span>
        <span className="text-xs" style={{ color: "#B0B5C0" }}>
          Connect data
        </span>
      </div>

      {/* Header */}
      <h2 className="text-[15px] font-semibold mb-1" style={{ color: "#181818" }}>
        Set up your workspace
      </h2>
      <p className="text-[13px] mb-6" style={{ color: "#5C6070" }}>
        Configure your workspace basics so we can tailor the experience for your team.
      </p>

      <form className="flex flex-col gap-5">
        <TextInput
          placeholder="https://yourapp.com"
          value={trackingDomain}
          description="Just the naked domain or subdomain without the 'www'"
          error={valid && trackingDomain !== "https://" && "Please enter a valid URL"}
          onChange={(e) => setTrackingDomain(e.currentTarget.value)}
          label={(
            <Flex align="center" className="gap-1">
              <Text size="sm" fw={500} style={{ color: "#2E3035" }}>Domain</Text>
              <Text span c="red.5" size="sm">*</Text>
              <Tooltip label="The primary URL of your application">
                <IconInfoCircle size={14} className="cursor-help" style={{ color: "#B0B5C0" }} />
              </Tooltip>
            </Flex>
          )}
          autoFocus
          styles={{
            input: { height: 36, borderColor: "#D0D0D0", borderRadius: 5, fontSize: 13 },
            description: { color: "#B0B5C0", fontSize: 12 },
          }}
        />
        <Select
          data={currencies.map((curr: any) => (
            { label: `${curr.name} (${curr.symbolNative})`, value: `${curr.code}` }
          ))}
          description="Used for revenue attribution and reporting."
          value={currency}
          searchable
          onChange={setCurrency}
          label={(
            <Flex align="center" className="gap-1">
              <Text size="sm" fw={500} style={{ color: "#2E3035" }}>Currency</Text>
              <Text span c="red.5" size="sm">*</Text>
              <Tooltip label="Helps accurately attribute revenue data to customers">
                <IconInfoCircle size={14} className="cursor-help" style={{ color: "#B0B5C0" }} />
              </Tooltip>
            </Flex>
          )}
          styles={{
            input: { height: 36, borderColor: "#D0D0D0", borderRadius: 5, fontSize: 13 },
            description: { color: "#B0B5C0", fontSize: 12 },
          }}
        />
        <div>
          <Flex align="center" className="gap-1 mb-1">
            <Text size="sm" fw={500} style={{ color: "#2E3035" }}>Timezone</Text>
            <Text span c="red.5" size="sm">*</Text>
          </Flex>
          <Text size="xs" mb={4} style={{ color: "#B0B5C0" }}>
            So we agree on what 'today' means.
          </Text>
          <TimezoneSelect
            id="timezone"
            value={timezone}
            onChange={setTimezone}
            styles={{
              control: (base: any) => ({
                ...base,
                minHeight: 36,
                borderColor: "#D0D0D0",
                borderRadius: 5,
                fontSize: 13,
                boxShadow: "none",
                "&:hover": { borderColor: "#8267E6" },
              }),
            }}
          />
        </div>
      </form>

      {/* Divider */}
      <div className="h-px mt-8 mb-5" style={{ backgroundColor: "#EEEEEE" }} />

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          leftSection={<IconLogout size={14} />}
          onClick={() => { logout(true); }}
          styles={{
            root: { height: 28, borderRadius: 5, fontSize: 12, fontWeight: 500, color: "#5C6070" },
          }}
        >
          Logout
        </Button>
        <Button
          size="xs"
          rightSection={<IconArrowRight size={14} />}
          disabled={!isFormValid}
          onClick={() => { submitHandler(); }}
          styles={{
            root: {
              height: 28,
              borderRadius: 5,
              fontSize: 12,
              fontWeight: 500,
              backgroundColor: isFormValid ? "#8267E6" : undefined,
              border: isFormValid ? "1px solid #6852BD" : undefined,
              "&:hover": { backgroundColor: "#755CD6" },
            },
          }}
        >
          Continue
        </Button>
      </div>
    </section>
  );
};

export default OnboardingWorkspace;
